import { z } from 'zod';
/** Advisor (backoffice) RBAC roles. */
export const AdvisorRole = {
    Admin: 'admin',
    Advisor: 'advisor',
    Backoffice: 'backoffice',
    /** MLG real-estate agent (Reto) — broker-side staff, backoffice-rank. */
    Makler: 'makler',
    /**
     * Verwaltung-only staff (owner-feedback 03-08): the platform account is
     * ONLY the anchor (tenant membership, session, audit, MFA) plus the SSO
     * jump into ONYX. Every CRM @Roles list deliberately EXCLUDES this role,
     * so such a user can reach nothing in the CRM itself — the tenant they
     * must be attached to is a technical home, not CRM access.
     */
    Verwaltung: 'verwaltung',
    /** Bau-Personal der MLG Generalunternehmung (Review 5d, PR #204) — Zugriff
     *  auf die BASALT-Produktflächen im Tenant `gu`. */
    Bau: 'bau',
};
export const advisorRoleSchema = z.enum([
    'admin',
    'advisor',
    'backoffice',
    'makler',
    'verwaltung',
    'bau',
]);
/**
 * Role hierarchy (higher rank = broader authority). Admin outranks all;
 * advisor sits above the operational roles; backoffice and makler are peers at
 * the operational rank. The `satisfies Record<AdvisorRole, …>` gives compile-time
 * exhaustiveness — a new AdvisorRole that forgets its rank/order fails to build.
 */
export const ROLE_RANK = {
    admin: 3,
    advisor: 2,
    backoffice: 1,
    makler: 1,
    // Bau-Personal: operative Stufe wie backoffice/makler — arbeitet auf den
    // BASALT-Produktflächen (anders als verwaltung, das reine Anker-Rolle ist).
    bau: 1,
    verwaltung: 0,
};
export const ROLE_ORDER = [
    'admin',
    'advisor',
    'backoffice',
    'makler',
    'bau',
    'verwaltung',
];
/** Principal kind for an authenticated session. */
export const PrincipalKind = {
    /** End-customer (portal). */
    Customer: 'customer',
    /** Internal advisor/backoffice user (CRM). */
    User: 'user',
};
/** Lead lifecycle. */
export const leadStatusSchema = z.enum([
    'new',
    'consult_requested', // calculator Beratung step — forward-only flip from `new`
    'contacted',
    'qualified',
    /**
     * @deprecated — wordt niet meer gezet sinds PR-1 Kundenportal; verwijdering
     * vereist datamigratie.
     *
     * De enige schrijver was `POST /leads/:id/invite` (owner-dossier 26-08-2026
     * §6: verwijderd). De waarde BLIJFT in de enum omdat bestaande rijen hem
     * dragen, de CRM-filters en de analytics-pipeline hem lezen en het label
     * «Eingeladen» in de statushistorie blijft staan. Een lead die nu wordt
     * uitgenodigd (`POST /customers/invite`) houdt zijn status: de uitnodiging is
     * een eigenschap van het KONTO geworden, niet van de lead.
     */
    'invited',
    'dossier_started',
    'dossier_completed',
    'matched',
    'won',
    'lost',
]);
/**
 * Which side of a transaction a lead is on (Email-Marketing Kauf-/Verkauf-Funnel,
 * WS-3a). Mirrors the Prisma `LeadSide` enum.
 */
export const leadSideSchema = z.enum(['buyer', 'seller', 'unknown']);
/**
 * Lead-Reifegrad (owner 26-8, `IAZI-LEAD-QUALIFIZIERUNG-VOORSTEL.md` v3): how
 * far a Bewertungs-funnel lead has proven itself, ORTHOGONAL to `status` (which
 * keeps describing the sales process). Ordered — every transition is forward-
 * only and the ORDER of this tuple IS the ladder (`reifegradRank` in the
 * backend reads it), so do not reorder.
 *
 *   rohanfrage          — funnel submit; invisible in work views, purged after
 *                         LEAD_ROHANFRAGE_TTL_DAGEN without progress
 *   telefon_bestaetigt  — phone OTP matched (the IAZI cost gate): visible in the
 *                         CRM, task + team notice fire HERE
 *   email_bestaetigt    — the report link was opened (double-opt-in as a
 *                         by-product; never a marketing consent on its own)
 *   qualifiziert        — booked consultation, «Übernehmen» or an advisor action
 *
 * Non-funnel leads (calculator gate, Beratung widget, manual) start at
 * `qualifiziert`: the ladder describes the funnel's self-verification, and a
 * lead that arrived through a human-typed callback request or an advisor's
 * hands has nothing left to prove on it.
 */
export const LEAD_REIFEGRADE = [
    'rohanfrage',
    'telefon_bestaetigt',
    'email_bestaetigt',
    'qualifiziert',
];
export const leadReifegradSchema = z.enum(LEAD_REIFEGRADE);
/** Financing request lifecycle. */
export const financingStatusSchema = z.enum([
    'draft',
    'in_progress',
    'submitted',
    'in_review',
    'matched',
    'approved',
    'rejected',
    'withdrawn',
]);
/** Consent record purposes (data-minimised, per-transfer consent). */
export const consentPurposeSchema = z.enum([
    'terms_of_service',
    'privacy_policy',
    'marketing_email',
    'data_processing',
    'bank_transfer', // explicit consent to forward a dossier to a specific bank
]);
